import React from 'react';
import { Copy, Check } from 'lucide-react';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';

interface CopyButtonProps {
  /** Function that returns the text to copy. Called lazily on click. */
  getText: () => string;
  /** Label shown next to icon. If omitted, renders icon-only with tooltip. */
  label?: string;
  className?: string;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  getText,
  label,
  className = '',
}) => {
  const { copied, copy } = useCopyToClipboard();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copy(getText());
  };

  // Icon-only variant (compact) — used on individual cards/items
  if (!label) {
    return (
      <button
        onClick={handleClick}
        className={`relative group/copy p-1 rounded transition-colors ${
          copied
            ? 'text-green-600 dark:text-green-400'
            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/30'
        } ${className}`}
        title={copied ? 'Copied!' : 'Copy'}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover/copy:opacity-100 pointer-events-none transition-opacity z-50">
          {copied ? 'Copied!' : 'Copy'}
        </span>
      </button>
    );
  }

  // Labelled variant — used in toolbar areas (next to Expand all / Collapse all)
  return (
    <button
      onClick={handleClick}
      className={`flex items-center gap-1 text-xs transition-colors ${
        copied
          ? 'text-green-600 dark:text-green-400'
          : 'text-gray-500 dark:text-medical-400 hover:text-cyan-600 dark:hover:text-cyan-400'
      } ${className}`}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      <span>{copied ? 'Copied!' : label}</span>
    </button>
  );
};
